import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Pipeline',
    width: '30%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },

  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    width: '10%',
    filter: {
      type: 'aggregation',
      aggregationName: 'env_aggregation',
      remoteSearchFields: ['env'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'env',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Type',
    width: '15%',
    filter: {
      type: 'fixed',
      remoteSearchFields: ['asset_type'],
      selectedValue: 'pipeline',
    },
  },
  {
    name: 'sourceSpecificMetadata.Airflow.owner',
    label: 'Owner',
    width: '15%',
  },
  { name: 'links', label: 'Airflow URL', width: '10%' },
  { name: 'lineage', label: 'Lineage', width: '10%' },
];

const attributesMap: Attribute[] = [
  {
    name: 'sourceSpecificMetadata.Airflow.airflow_url',
    label: 'Airflow URL',
    type: 'link',
    showInColumn: 0,
    description: 'Airflow URL',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.env',
    label: 'Environment',
    showInColumn: 0,
    description: 'Asset environment',
  },
  {
    name: 'technicalMetadata.asset_type',
    label: 'Asset Type',
    showInColumn: 0,
    description: 'Asset type',
  },
  {
    name: 'technicalMetadata.operationalMetadata.asset_daily_cost',
    label: 'DAG Daily Cost',
    type: 'string',
    prefix: '$',
    showInColumn: 1,
    description: 'DAG Daily Cost',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.pipeline',
    label: 'Pipeline',
    showInColumn: 0,
    description: 'Pipeline name',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.owner',
    label: 'Owner',
    showInColumn: 1,
    description: 'Asset owner',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Airflow.cluster',
    label: 'Cluster',
    showInColumn: 1,
    description: 'Asset cluster',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.Airflow.sla',
    label: 'SLA',
    showInColumn: 2,
    description: 'Asset SLA',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.tags',
    label: 'Tags',
    showInColumn: 2,
    description: 'Asset tags',
    type: 'table',
    tableColumns: ['Name', 'Value'],
    tableFieldMapping: {
      Name: 'name',
      Value: 'value',
    },
  },
  {
    name: 'sourceSpecificMetadata.Airflow.scan_date',
    label: 'Scan Date',
    showInColumn: 1,
    description: 'Scan date',
  },

  {
    name: 'sourceSpecificMetadata.Airflow.access_control',
    label: 'Access Control',
    showInColumn: 1,
    description: 'Access Control',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.catchup',
    label: 'Catchup',
    showInColumn: 1,
    description: 'Catchup',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.description',
    label: 'Description',
    showInColumn: 1,
    description: 'Description',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.doc_md',
    label: 'Doc Md',
    type: 'markdown',
    showInColumn: 1,
    description: 'Doc Md',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.fileloc',
    label: 'Fileloc',
    showInColumn: 1,
    description: 'Fileloc',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.is_paused_upon_creation',
    label: 'Is Paused Upon Creation',
    showInColumn: 1,
    description: 'Is Paused Upon Creation',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.start_date',
    label: 'Start Date',
    showInColumn: 1,
    description: 'Asset start date',
  },
  {
    name: 'sourceSpecificMetadata.Airflow.timezone',
    label: 'Time Zone',
    showInColumn: 1,
    description: 'Time Zone',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_executed',
    label: 'Last Executed',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_run_status',
    label: 'Last Run Status',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_start_time',
    label: 'Last Start Time',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_end_time',
    label: 'Last End Time',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_data_interval_start',
    label: 'Last Data Interval Start',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_data_interval_end',
    label: 'Last Data Interval End',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.avg_run_time',
    label: 'AVG Run Time',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.last_run_by',
    label: 'Last Run By',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.success_rate',
    label: 'Success Rate',
    showInColumn: 1,
  },
  {
    name: 'technicalMetadata.usageMetadata.failure_rate',
    label: 'Failure Rate',
    showInColumn: 1,
  },

  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Technical Description',
    description: 'Technical Description',
    showInColumn: 2,
  },
  {
    name: 'sourceSpecificMetadata.Airflow.tasks',
    description: 'List of tasks for this Airflow DAG',
    label: 'Tasks',
    type: 'table',
    tableColumns: ['Task ID', 'Task Module', 'Task Type', 'Downstream Task IDs', 'SQL'],
    tableFieldMapping: {
      'Task ID': 'task_id',
      'Task Module': 'task_module',
      'Task Type': 'task_type',
      'Downstream Task IDs': 'downstream_task_ids',
      SQL: 'sql',
    },
  },
];

const customQueryForListPage = `
  Airflow {
    cluster
    owner
    airflow_url
  }
`;

const customQueryForDetailsPage = `
  Airflow {
    access_control
    airflow_url
    catchup
    cluster
    description
    doc_md
    fileloc
    is_paused_upon_creation
    owner
    pipeline
    scan_date
    sla
    start_date
    tags {
      name
      value
    }
    tasks {
      downstream_task_ids
      sql
      task_id
      task_module
      task_type
    }
    timezone
  }
`;

const searchableFields = getSearchableFields(attributesMap);

export const AirflowConfig: DataSource = {
  id: '3',
  name: 'Airflow',
  showColumns: false,
  showPartitionColumns: false,
  showLineage: true,
  showLineageTable: false,
  showLineagePipeline: true,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: false,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
};
